import {
  getPatientPagePath,
  normalizePatientHn,
} from "../../../utils/patientPageUtils";

const getEventAttribute = (eventEl, name, fallback = "") =>
  eventEl.getAttribute(name) ||
  eventEl.getAttribute(`data-${name}`) ||
  fallback;

export const normalizeBookingHn = normalizePatientHn;
export { getPatientPagePath };

// Exclude retired form fields even when copying or editing a legacy booking.
export const getBookingExtendedPropsForSave = (extendedProps = {}) =>
  Object.fromEntries(Object.entries(extendedProps || {}).filter(([key]) => key !== "sweatArtifact"));

export const getDefaultBookingCustomer = (nodeName, customers = []) => {
  const chgCustomer = "Chularat 3 International Hospital";
  if (
    ["CHG", "Chularat Hospital Group"].includes(nodeName) &&
    customers.includes(chgCustomer)
  ) {
    return chgCustomer;
  }
  return customers[0] || "";
};

export const trimBookingHnInput = (event) => {
  const input = event && (event.currentTarget || event.target);
  if (!input) return "";

  const normalizedHn = normalizeBookingHn(input.value);
  input.value = normalizedHn;
  return normalizedHn;
};

export const buildExternalBookingEventData = (eventEl) => ({
  title: getEventAttribute(eventEl, "title"),
  id: getEventAttribute(eventEl, "id", getEventAttribute(eventEl, "data")),
  color: getEventAttribute(eventEl, "color", "green"),
  allDay: true,
  extendedProps: {
    order: getEventAttribute(eventEl, "order"),
    hn: getEventAttribute(eventEl, "hn"),
    type: getEventAttribute(eventEl, "type"),
    status: getEventAttribute(eventEl, "status"),
    node: getEventAttribute(eventEl, "node"),
    customer: getEventAttribute(eventEl, "customer"),
  },
});

export const createBookingEventDraft = (eventData = {}) => {
  const draft = {
    title: eventData.title || "",
    id: eventData.id || "",
    start: eventData.startStr || eventData.start || "",
    startStr: eventData.startStr || eventData.start || "",
    end: eventData.end || null,
    color: eventData.color || eventData.backgroundColor || "green",
    backgroundColor: eventData.backgroundColor || eventData.color || "green",
    borderColor:
      eventData.borderColor ||
      eventData.backgroundColor ||
      eventData.color ||
      "green",
    allDay: eventData.allDay !== false,
    extendedProps: {
      ...(eventData.extendedProps || {}),
    },
  };

  draft.setExtendedProp = (key, value) => {
    draft.extendedProps[key] = value;
  };

  draft.setProp = (key, value) => {
    draft[key] = value;
  };

  draft.setStart = (value) => {
    draft.start = value;
    draft.startStr = typeof value === "string" ? value : "";
  };

  draft.setEnd = (value) => {
    draft.end = value;
  };

  draft.remove = () => {};

  draft.toPlainObject = () => ({
    title: draft.title,
    id: draft.id,
    start: draft.start,
    startStr: draft.startStr,
    end: draft.end,
    color: draft.color,
    backgroundColor: draft.backgroundColor,
    borderColor: draft.borderColor,
    allDay: draft.allDay,
    extendedProps: { ...draft.extendedProps },
  });

  return draft;
};

export const ensureBookingEventDraft = (eventData = {}) => {
  try {
    if (
      eventData &&
      eventData.setExtendedProp &&
      eventData.setProp &&
      eventData.extendedProps
    ) {
      return eventData;
    }
  } catch (err) {
    return createBookingEventDraft();
  }

  return createBookingEventDraft(eventData || {});
};

export const getPatientSearchBookingLocation = (
  eventData = {},
  patientData = {},
) => {
  const bookingProps = eventData.extendedProps || {};

  return {
    node: patientData.node || bookingProps.node || "",
    customer: patientData.customer || bookingProps.customer || "",
  };
};

const getBookingConfirmationValue = (value, fallback = "-") =>
  String(value || "").trim() || fallback;

export const getBookingAssignmentConfirmationData = (
  existingData = {},
  currentData = {},
  updatedBy = "",
) => {
  const existingProps = existingData.extendedProps || {};
  const currentProps = currentData.extendedProps || {};
  const patientName = [
    currentProps.firstname || existingProps.firstname,
    currentProps.lastname || existingProps.lastname,
  ]
    .map((name) => String(name || "").trim())
    .filter(Boolean)
    .join(" ");
  const oldStatus = getBookingConfirmationValue(existingProps.status);
  const newStatus = getBookingConfirmationValue(currentProps.status);
  const oldTechnician = getBookingConfirmationValue(
    existingProps.technician,
    "Not selected",
  );
  const newTechnician = getBookingConfirmationValue(
    currentProps.technician,
    "Not selected",
  );
  const oldDoctor = getBookingConfirmationValue(
    existingProps.doctor,
    "Not selected",
  );
  const newDoctor = getBookingConfirmationValue(
    currentProps.doctor,
    "Not selected",
  );

  return {
    order: getBookingConfirmationValue(
      currentProps.order || existingProps.order || currentData.title,
    ),
    patientName: patientName || "-",
    oldStatus,
    newStatus,
    oldTechnician,
    newTechnician,
    oldDoctor,
    newDoctor,
    statusChanged: oldStatus !== newStatus,
    technicianChanged: oldTechnician !== newTechnician,
    doctorChanged: oldDoctor !== newDoctor,
    previousUpdatedBy: getBookingConfirmationValue(
      existingProps.updatedBy || existingProps.createBy,
      "Unknown user",
    ),
    updatedBy: getBookingConfirmationValue(updatedBy, "Unknown user"),
  };
};

export const getBookingEventDisplayColor = (
  eventData = {},
  statusList = [],
  colorList = [],
) => {
  const props = eventData.extendedProps || {};

  if (props.isBookingMoreLink) {
    return eventData.backgroundColor || eventData.color || "#ffffff";
  }

  if (props.status) {
    const statusIndex = statusList.findIndex((status) => status === props.status);
    if (statusIndex >= 0 && colorList[statusIndex]) {
      return colorList[statusIndex];
    }
  }

  return eventData.backgroundColor || eventData.color || "green";
};

export const getBookingEventColorProps = (
  eventData = {},
  statusList = [],
  colorList = [],
) => {
  const displayColor = getBookingEventDisplayColor(
    eventData,
    statusList,
    colorList,
  );

  return {
    color: displayColor,
    backgroundColor: displayColor,
    borderColor: displayColor,
  };
};
